import {
  ColDef,
  GridOptions,
  GridApi,
  ICellRendererParams,
} from 'ag-grid-enterprise';
import { GridReadyEvent, ITooltipParams } from 'ag-grid-community';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Features } from '../../../types/features';
import { CrossReferencePermissions } from '../../../types/permissions';
import {
  CrossReference,
  CrossReferencesOpinionOption,
} from '../../../types/product';
import {
  CrossReferenceType,
  CreatedByOrgTypeEnum,
  CrossOpinionEnum,
  User,
  Identifier,
} from '../../../types/types';
import {
  Sizes,
  GridTemplateService,
  CustomLoadingOverlayComponent,
  CustomOverlayNoRowsComponent,
  GridClinicalAssessmentWrapperComponent,
  GridVizientAssessmentWrapperComponent,
  GridTooltipComponent,
  CategoryStructureTooltipComponent,
} from '../../modules';
import { CrossReferencesOptionsRendererComponent } from './cross-references-options-renderer/cross-references-options-renderer.component';
import { ProductDetailRouteService } from '../../../services/product-detail-route.service';
import { insertIf } from '../../../utils';
import { CrossReferencesProductRendererComponent } from './cross-references-product-renderer/cross-references-product-renderer.component';
import {
  SellerCellRendererComponent,
  SellerCellRendererComponentParams,
} from './seller-cell-renderer.component';
import {
  TypeCellRendererComponent,
  TypeCellRendererComponentParams,
} from './type-cell-renderer.component';

@Component({
    selector: 'app-cross-references-grid',
    templateUrl: './cross-references-grid.component.html',
    styleUrls: ['./cross-references-grid.component.scss'],
    standalone: false
})
export class CrossReferencesGridComponent implements OnInit {
  // Grid Variables
  columnDefs!: Array<ColDef>;
  gridOptions!: GridOptions;
  gridApi?: GridApi;
  Sizes = Sizes;
  productGridNewFieldsFlag?: true;

  /**
   * @fixme Add partial for cross-reference.
   */
  @Input() crossReferences!: CrossReference[];
  @Input() crossReferenceOpinionOptions = [] as CrossReferencesOpinionOption[];
  @Input() permissions!: CrossReferencePermissions;
  @Input() userAccount!: User;
  @Input() categorizationSchemeId!: number;
  @Input() categoryId!: number;
  @Input() productId!: number;
  @Input() features?: Features;
  @Input() set isLoadingSearchResults(isLoading: boolean) {
    isLoading
      ? this.gridApi?.setGridOption('loading', true)
      : this.gridApi?.setGridOption('loading', false);
  }
  @Input() isShowingSearchResults = false;

  @Output() promoteCrossEvent = new EventEmitter<CrossReference>();
  @Output() removeCrossEvent = new EventEmitter<CrossReference>();
  @Output() addCrossEvent = new EventEmitter<CrossReference>();
  @Output() updateOpinionEvent = new EventEmitter<{
    crossReference: CrossReference;
    willDeleteCross: boolean;
  }>();

  constructor(
    private gridTemplateService: GridTemplateService,
    private productDetailRoute: ProductDetailRouteService,
  ) {}

  ngOnInit(): void {
    this.columnDefs = this.getColumnDefs();
    this.gridOptions = this.getGridOptions();
  }

  promoteCross(promotedCross: CrossReference) {
    this.promoteCrossEvent.emit(promotedCross);
  }

  removeCross(removedCross: CrossReference) {
    this.removeCrossEvent.emit(removedCross);
  }

  addCross(crossReference: CrossReference) {
    this.addCrossEvent.emit(crossReference);
  }

  /*
   * Grid Functions
   */

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  getGridOptions(): GridOptions {
    return {
      accentedSort: true,
      defaultColDef: {
        filter: true,
        flex: 1,
        sortable: true,
        tooltipComponent: 'gridTooltipComponent',
      },
      components: {
        ...CategoryStructureTooltipComponent.frameworkComponentProp(),
        crossReferencesOptionsRenderer: CrossReferencesOptionsRendererComponent,
        customLoadingOverlayComponent: CustomLoadingOverlayComponent,
        customOverlayNoRowsComponent: CustomOverlayNoRowsComponent,
        gridClinicalAssessmentWrapperComponent:
          GridClinicalAssessmentWrapperComponent,
        gridVizientAssessmentWrapperComponent:
          GridVizientAssessmentWrapperComponent,
        gridTooltipComponent: GridTooltipComponent,
      },
      loadingOverlayComponent: 'customLoadingOverlayComponent',
      noRowsOverlayComponent: 'customOverlayNoRowsComponent',
      noRowsOverlayComponentParams: {
        noRowsMessageFunc: () => {
          return 'No crosses found';
        },
      },
      rowHeight: 75,
      suppressRowTransform: true,
    };
  }

  getColumnDefs() {
    const hasPermissionToAddVizientOpinions =
      this.userAccount?.permissions?.includes('ROLE_VIZIENT_VALUE_ANALYSIS') &&
      this.userAccount?.isGPO;
    return [
      {
        headerName: '',
        headerComponentParams: { template: this.getEditCrossHeaderTemplate() },
        field: 'editCross',
        hide: !this.userAccount.isSeller,
        minWidth: 70,
        maxWidth: 70,
        suppressHeaderFilterButton: true,
        sortable: false,
        cellRenderer: 'crossReferencesOptionsRenderer',
        cellRendererParams: () => ({
          userAccount: this.userAccount,
          promoteCross: (crossReference: CrossReference) =>
            this.promoteCross(crossReference),
          removeCross: (crossReference: CrossReference) =>
            this.removeCross(crossReference),
        }),
      },
      ...insertIf(!this.features?.CONSUME_PRODUCT_DATA_MD_CAT_964488, {
        headerName: 'Crosses',
        tooltipComponent: 'gridTooltipComponent',
        tooltipValueGetter: ({ data }: { data: CrossReference }) =>
          `${data.toProduct?.catalogNumber} ${data.toProduct?.description}`,
        field: 'crossReference',
        valueGetter: ({ data }: { data: CrossReference }) =>
          data?.toProduct?.catalogNumber,
        cellClass: 'pt-2',
        cellRenderer: CrossReferencesProductRendererComponent,
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            showCross:
              params.data?.crossReferenceType !== CrossReferenceType.NO_MATCH,
            product: params.data?.toProduct?.catalogNumber ?? '',
            content: params.data?.toProduct?.description ?? '',
            productUrl: this.productDetailRoute.get(
              {
                categorizationSchemeId: this.categorizationSchemeId,
                categoryId: this.categoryId,
                productId: params.data?.toProduct?.id,
                productSource: params.data?.toProduct?.productSource,
              },
              { features: this.features },
            ),
            status: params.data?.toProduct?.status ?? '',
            isAptitudeProduct:
              params.data?.toProduct?.productSource === 'aptitude',
          };
        },
      }),
      ...insertIf(this.features?.CONSUME_PRODUCT_DATA_MD_CAT_964488, {
        headerName: 'Manufacturer Catalog Number',
        colId: 'manufacturerCatalogNuber',
        tooltipComponent: 'gridTooltipComponent',
        tooltipValueGetter: ({ data }: { data: CrossReference }) =>
          `${data.toProduct?.manufacturerCatalogNumber} ${data.toProduct?.description}`,
        valueGetter: ({ data }: { data: CrossReference }) =>
          data?.toProduct?.manufacturerCatalogNumber,
        cellClass: 'pt-2',
        cellRenderer: CrossReferencesProductRendererComponent,
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            showCross:
              params.data?.crossReferenceType !== CrossReferenceType.NO_MATCH,
            product: params.data?.toProduct?.manufacturerCatalogNumber ?? '',
            content: params.data?.toProduct?.description ?? '',
            productUrl: this.productDetailRoute.get(
              {
                categorizationSchemeId: this.categorizationSchemeId,
                categoryId: this.categoryId,
                productId: params.data?.toProduct?.id,
                productSource: params.data?.toProduct?.productSource,
              },
              { features: this.features },
            ),
            status: params.data?.toProduct?.status ?? '',
            isAptitudeProduct:
              params.data?.toProduct?.productSource === 'aptitude',
          };
        },
      }),
      ...insertIf(this.features?.CONSUME_PRODUCT_DATA_MD_CAT_964488, {
        headerName: 'Distributor Catalog Number',
        colId: 'distributorCatalogNumber',
        tooltipComponent: 'gridTooltipComponent',
        tooltipValueGetter: ({ data }: { data: CrossReference }) =>
          `${data.toProduct?.catalogNumber} ${data.toProduct?.description}`,
        valueGetter: ({ data }: { data: CrossReference }) =>
          data?.toProduct?.catalogNumber,
        cellClass: 'pt-2',
        cellRenderer: CrossReferencesProductRendererComponent,
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            showCross:
              params.data?.crossReferenceType !== CrossReferenceType.NO_MATCH,
            product: params.data?.toProduct?.catalogNumber ?? '',
            content: params.data?.toProduct?.description ?? '',
            productUrl: this.productDetailRoute.get(
              {
                categorizationSchemeId: this.categorizationSchemeId,
                categoryId: this.categoryId,
                productId: params.data?.toProduct?.id,
                productSource: params.data?.toProduct?.productSource,
              },
              { features: this.features },
            ),
            status: params.data?.toProduct?.status ?? '',
            isAptitudeProduct:
              params.data?.toProduct?.productSource === 'aptitude',
          };
        },
      }),
      ...insertIf(!this.features?.CONSUME_PRODUCT_DATA_MD_CAT_964488, {
        headerName: 'Seller',
        colId: 'seller',
        tooltipComponent: 'gridTooltipComponent',
        tooltipValueGetter: ({ data }: { data: CrossReference }) =>
          data?.toProduct?.seller?.name || '',
        valueGetter: ({ data }: { data: CrossReference }) => {
          return data?.toProduct?.seller?.name || '';
        },
        cellClass: 'pt-2',
        cellRenderer: SellerCellRendererComponent,
        cellRendererParams: ({
          data,
        }: ICellRendererParams<CrossReference>): SellerCellRendererComponentParams => ({
          manufacturerName: data?.toProduct?.seller?.name || '',
          sellerName: data?.toProduct?.sysSeller?.name || '',
        }),
      }),
      ...insertIf(this.features?.CONSUME_PRODUCT_DATA_MD_CAT_964488, {
        headerName: 'Manufacturer',
        colId: 'manufacturerName',
        tooltipComponent: 'gridTooltipComponent',
        tooltipValueGetter: ({ data }: { data: CrossReference }) =>
          data?.toProduct?.manufacturer?.name || '',
        valueGetter: ({ data }: { data: CrossReference }) => {
          return data?.toProduct?.manufacturer?.name || '';
        },
        cellClass: 'pt-2',
        cellRenderer: SellerCellRendererComponent,
        cellRendererParams: ({
          data,
        }: ICellRendererParams<CrossReference>): SellerCellRendererComponentParams => ({
          manufacturerName: data?.toProduct?.manufacturer?.name || '',
          sellerName: data?.toProduct?.parentManufacturer?.name || '',
        }),
      }),
      ...insertIf(this.features?.CONSUME_PRODUCT_DATA_MD_CAT_964488, {
        headerName: 'Distributor',
        colId: 'distributorName',
        tooltipComponent: 'gridTooltipComponent',
        tooltipValueGetter: ({ data }: { data: CrossReference }) =>
          data?.toProduct?.distributor?.name || '',
        valueGetter: ({ data }: { data: CrossReference }) =>
          data?.toProduct?.distributor?.name || '',
        cellClass: 'pt-2',
        cellRenderer: SellerCellRendererComponent,
        cellRendererParams: ({
          data,
        }: ICellRendererParams<CrossReference>): SellerCellRendererComponentParams => ({
          manufacturerName: data?.toProduct?.distributor?.name || '',
          sellerName: data?.toProduct?.parentDistributor?.name || '',
        }),
        sort: 'asc',
      }),
      {
        headerName: 'Category',
        colId: 'category',
        ...CategoryStructureTooltipComponent.columnPropsForComponent(
          (params: ITooltipParams) => {
            const toProduct = (params.data as CrossReference).toProduct;
            let categories = [] as Identifier[];

            if (toProduct.category?.name && toProduct.subCategory?.name) {
              categories = [toProduct.category, toProduct.subCategory];
            } else {
              categories = this.gridTemplateService.buildCategories(
                toProduct?.categories?.[0],
              );
            }

            return categories;
          },
        ),
        valueGetter: ({ data }: { data: CrossReference }) => {
          const toProduct = data?.toProduct;
          let exportValue = [toProduct?.category, toProduct?.subCategory];

          if (!exportValue) {
            exportValue = this.gridTemplateService.buildCategories(
              toProduct?.categories?.[0],
            );
          }

          return exportValue.map((category) => category?.name).join(' > ');
        },
        filterValueGetter: ({ data }: { data: CrossReference }) =>
          this.getLowestCategoryLeafName(data),
        valueFormatter: ({ data }: { data: CrossReference }) =>
          this.getLowestCategoryLeafName(data),
      },
      {
        headerName: 'Brand Name',
        colId: 'brandName',
        tooltipComponent: 'gridTooltipComponent',
        tooltipValueGetter: ({ data }: { data: CrossReference }) =>
          data?.toProduct?.brandName || '',
        valueGetter: ({ data }: { data: CrossReference }) =>
          data?.toProduct?.brandName || '',
      },
      {
        headerName: 'Type',
        field: 'type',
        minWidth: 250,
        tooltipComponent: 'gridTooltipComponent',
        valueGetter: ({ data }: { data: CrossReference }) => {
          const crossTypeDesc = TypeCellRendererComponent.getCrossTypeDesc(
            data?.crossReferenceDescription,
          );
          return crossTypeDesc.desc;
        },
        cellClass: 'pt-2',
        cellRenderer: TypeCellRendererComponent,
        cellRendererParams: ({
          data,
        }: ICellRendererParams<CrossReference>): TypeCellRendererComponentParams => ({
          userAccount: this.userAccount,
          crossReference: data!,
        }),
      },
      ...insertIf(this.userAccount.isGPO, {
        headerName: 'Vizient Reviewed',
        cellClass: 'grid-overflow-visible',
        cellRenderer: 'gridVizientAssessmentWrapperComponent',
        cellRendererParams: (params: ICellRendererParams) => ({
          updateVizientAssessment: (crossReference: CrossReference) => {
            const willDeleteCross =
              crossReference.gpoOpinion?.opinion === CrossOpinionEnum.DISPUTE &&
              crossReference.crossReferenceDescription === 'Vizient Cross';
            if (
              crossReference.gpoOpinion?.opinion &&
              crossReference.createdByOrgType === 'SELLER'
            ) {
              params.node.setData(crossReference);
            }
            this.updateOpinionEvent.emit({
              crossReference,
              willDeleteCross: willDeleteCross,
            });
          },
        }),
        valueGetter: ({ data }: { data: CrossReference }) => {
          return {
            vizientAssessmentGridData: {
              crossReference: data,
              user: this.userAccount,
              crossReferenceOpinionOptions: this.crossReferenceOpinionOptions,
              productId: this.productId,
              hasPermissionToAddOpinions: hasPermissionToAddVizientOpinions,
            },
          };
        },
      }),
      {
        headerName: 'Clinical Assessment',
        cellClass: 'grid-overflow-visible',
        cellRenderer: 'gridClinicalAssessmentWrapperComponent',
        cellRendererParams: () => ({
          addCross: (crossReference: CrossReference) => {
            this.addCross(crossReference);
          },
          updateClinicalAssessment: (crossReference: CrossReference) => {
            // If this cross is down voted and it was created by the current user's org
            // then delete the cross.
            const willDeleteCross =
              crossReference.createdByOrgType.toUpperCase() !==
                CreatedByOrgTypeEnum.SELLER &&
              crossReference.opinion?.opinion === CrossOpinionEnum.DISPUTE &&
              this.userAccount.organization.id ===
                crossReference.createdByOrg.id;

            this.updateOpinionEvent.emit({ crossReference, willDeleteCross });
          },
        }),
        valueGetter: ({ data }: { data: CrossReference }) => {
          return {
            clinicalAssessmentGridData: {
              crossReference: data,
              permissions: this.permissions,
              user: this.userAccount,
              crossReferenceOpinionOptions: this.crossReferenceOpinionOptions,
              features: this.features,
              productId: this.productId,
              isShowingSearchResults: this.isShowingSearchResults,
            },
          };
        },
      },
    ] as ColDef[];
  }

  /*
   * Template Helper Functions
   */

  private getLowestCategoryLeafName(crossReference: CrossReference) {
    let categoryName = crossReference?.toProduct?.subCategory?.name;

    if (!categoryName) {
      categoryName = crossReference?.toProduct?.categories?.[0]?.name;
    }

    return categoryName ?? '';
  }

  private getEditCrossHeaderTemplate() {
    return `<div class="ag-cell-label-container ag-column-header-for-two-line-text" role="presentation">
              <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
              <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
              <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
                <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
                <i class="fa fa-edit"></i>
                <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
              </div>
            </div>`;
  }
}
